import React, { useState, useCallback, useEffect, useContext } from "react";
import { useLocation, useParams, useNavigate, UNSAFE_NavigationContext } from "react-router-dom";
import AirHeader from "../../components/AirHeader";
import AirTitle from "../../components/AirTitle";
import Menu from "../../components/menu/Menu";
import Modal from "../../components/overlay/Modal";
import ConfirmModal from "../../components/overlay/ConfirmModal";
import { Mode } from "../tutorV2/types/tutor.types";
import Btn from "../../components/btn";
import { Chevron, Download } from "../../assets/svgs/icons";
import useSocratesV2View from "./hooks/useSocratesV2View";
import SocratesChatContainer from "./containers/SocratesChatContainer";
import SocratesPromptContainer from "./containers/SocratesPromptContainer";
import SocratesInterviewList from "./containers/SocratesInterviewList";
import SocratesCreateContainer from "./containers/SocratesCreateContainer";
import SocratesResultContainer from "./containers/SocratesResultContainer";
import { useAutoScroll } from "../tutorV2/hooks/useAutoScroll";
import useShareView from "../tutorV2/hooks/presentation/useShareView";
import { useAuth } from "../tutorV2/hooks/useAuth";
import { useSocratesInterview } from "../../hooks/useSocratesInterview";
import LoginModal from "../chat/modal/LoginModal";
import { MyContext } from "../../context/MyContext";
import Book from "../../assets/icons/icon_book.gif";

const SocratesV2 = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isCreatePage = location.pathname === "/socrates/new";
  const isResultPage = location.pathname.endsWith("/result");

  const {
    isMd,
    isLoggedIn,
    currentInterviewId,
    isRestarting,
    isGeneratingResult,
    isStartingInterview,
    interviewProgress,
    handleLoginClick,
    handleCreateSocrates,
    handleStartInterview,
    interview,
  } = useSocratesV2View();

  const { redirectToLogin } = useAuth();
  const { pauseInterview } = useSocratesInterview();
  // 튜터와 동일하게 비로그인 답변 제출 시 로그인 모달을 노출하기 위해 필요
  const { isLogin } = useContext(MyContext);

  // 면접 이탈 확인 팝업 상태
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  // 차단된 네비게이션을 저장 (확인 후 실행)
  const pendingNavigationRef = React.useRef<(() => void) | null>(null);

  // BrowserRouter 환경에서 내부 네비게이션 차단
  const { navigator } = useContext(UNSAFE_NavigationContext);

  useEffect(() => {
    if (interview?.status !== "active") return;

    const originalPush = navigator.push;
    const originalReplace = navigator.replace;

    navigator.push = (...args: Parameters<typeof originalPush>) => {
      pendingNavigationRef.current = () => originalPush.apply(navigator, args);
      setShowExitConfirm(true);
    };

    navigator.replace = (...args: Parameters<typeof originalReplace>) => {
      pendingNavigationRef.current = () => originalReplace.apply(navigator, args);
      setShowExitConfirm(true);
    };

    return () => {
      navigator.push = originalPush;
      navigator.replace = originalReplace;
    };
  }, [navigator, interview?.status]);

  // 브라우저 탭 닫기/새로고침 차단
  useEffect(() => {
    if (interview?.status !== "active") return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [interview?.status]);

  // 면접 이탈 확인 → 중단하고 나가기
  const handleExitConfirm = useCallback(async () => {
    // onOpenChange(false)가 먼저 실행되어 ref가 null이 되므로 미리 저장
    const pendingNavigation = pendingNavigationRef.current;
    try {
      await pauseInterview();
    } catch (error) {
      console.error("면접 중단 실패:", error);
    }
    setShowExitConfirm(false);
    pendingNavigationRef.current = null;
    if (pendingNavigation) {
      pendingNavigation();
    }
  }, [pauseInterview]);

  // 면접 이탈 취소 → 면접 화면 유지
  const handleExitCancel = useCallback((open: boolean) => {
    if (!open) {
      setShowExitConfirm(false);
      pendingNavigationRef.current = null;
    }
  }, []);

  // 모바일 비로그인 상태 확인
  const isMobileGuest = !isMd && !isLoggedIn;

  // 토글 버튼 핸들러
  const handleToggleToTutor = () => {
    navigate("/tutor");
  };

  const {
    scrollRef,
    isFreeScroll,
    fetching,
    hasMessages,
    disableFreeScrollAndScroll,
  } = useAutoScroll();

  // Share modal
  const { shareModalOpen, handleOpenChange, shareUrl, copySuccess, onCopy } =
    useShareView();

  // /socrates/new 경로: 소크라테스 생성 폼 페이지
  if (isCreatePage) {
    return (
      <div className="flex flex-row h-full w-full bg-white overflow-hidden">
        {/* Sidebar */}
        {isLoggedIn ? <Menu mode={Mode.SOCRATES} /> : null}

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <SocratesCreateContainer />
        </div>
      </div>
    );
  }

  // /socrates/:id/result 경로: 면접 결과 페이지
  if (isResultPage && id) {
    // 다운로드 버튼 핸들러
    const handleDownloadClick = () => {
      if ((window as any).__socratesDownloadHandler) {
        (window as any).__socratesDownloadHandler();
      }
    };

    return (
      <div className="flex flex-row h-full w-full bg-white overflow-hidden">
        {/* Sidebar */}
        {isLoggedIn ? <Menu mode={Mode.SOCRATES} /> : null}

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* Header */}
          <div className="relative">
            <AirHeader type="main" />
            {/* PC: Download Button */}
            {isMd && (
              <button
                onClick={handleDownloadClick}
                className="absolute top-1/2 -translate-y-1/2 right-0 p-4"
                aria-label="다운로드"
              >
                <Download className="w-6 h-6 text-[#212121]" />
              </button>
            )}
          </div>

          {/* Result Content */}
          <div className="flex-1 overflow-y-auto px-4 md:px-8 py-4 md:py-8 pb-4 md:pb-4">
            <SocratesResultContainer />
          </div>
        </div>
      </div>
    );
  }

  // 면접 재시작 중 로딩 화면 (처음부터 버튼 클릭 시)
  if (isRestarting) {
    return (
      <div className="flex flex-row h-full w-full bg-white overflow-hidden">
        {/* Sidebar */}
        {isLoggedIn ? <Menu mode={Mode.SOCRATES} /> : null}

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* Header */}
          <AirHeader type="main" />

          {/* Loading Content */}
          <div className="flex-1 flex items-center justify-center">
            <div className="flex items-center gap-2 text-base font-bold">
              <img src={Book} alt="loading" className="w-8" />
              <p>면접을 준비하는 중입니다...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 결과 생성 중 로딩 화면 (결과보기 버튼 클릭 시)
  if (isGeneratingResult) {
    return (
      <div className="flex flex-row h-full w-full bg-white overflow-hidden">
        {/* Sidebar */}
        {isLoggedIn ? <Menu mode={Mode.SOCRATES} /> : null}

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* Header */}
          <AirHeader type="main" />

          {/* Loading Content */}
          <div className="flex-1 flex items-center justify-center">
            <div className="flex items-center gap-2 text-base font-bold">
              <img src={Book} alt="loading" className="w-8" />
              <p>결과를 생성하는 중입니다...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 면접 카드 클릭 시 로딩 화면
  if (isStartingInterview) {
    return (
      <div className="flex flex-row h-full w-full bg-white overflow-hidden">
        {/* Sidebar */}
        {isLoggedIn ? <Menu mode={Mode.SOCRATES} /> : null}

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* Header */}
          <AirHeader type="main" />

          {/* Loading Content */}
          <div className="flex-1 flex items-center justify-center">
            <div className="flex items-center gap-2 text-base font-bold">
              <img src={Book} alt="loading" className="w-8" />
              <p>면접을 준비하는 중입니다...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // PC 로그인 후: 사이드바 + 메인 영역
  // 모바일에서는 HeaderMo(56px)가 App 레벨에서 렌더링되고, AirHeader(56px)가 추가로 렌더링됨
  return (
    <div className="flex flex-row h-full w-full bg-white overflow-hidden">
      {/* Sidebar */}
      {isLoggedIn ? <Menu mode={Mode.SOCRATES} /> : null}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden relative">
        {/* Header */}
        <AirHeader type="main" progress={interviewProgress} />

        {/* Main Content Container - 스크롤 영역 */}
        <div
          ref={scrollRef}
          className={`flex-1 overflow-y-auto px-4 min-h-0 ${
            !isMd ? "pt-4 pb-0" : "py-8"
          }`}
        >
          <div
            className={`flex flex-col gap-4 md:gap-8 w-full h-full items-center ${
              currentInterviewId ? "" : "md:justify-center"
            }`}
          >
            {/* 모바일: 토글 버튼 (면접 진행 중이 아닐 때만 표시) */}
            {!isMd && !currentInterviewId && (
              <div className="flex items-center justify-center shrink-0">
                <div className="flex items-center justify-center bg-[#f7f7f7] border border-[#eee] rounded-[40px]">
                  <button
                    className="flex items-center justify-center px-5 py-2"
                    onClick={handleToggleToTutor}
                  >
                    <span className="text-sm font-medium text-[#888] tracking-[-0.28px]">
                      튜터
                    </span>
                  </button>
                  <button className="flex items-center justify-center px-5 py-2 bg-[#212121] rounded-[40px]">
                    <span className="text-sm font-bold text-white tracking-[-0.28px]">
                      소크라테스
                    </span>
                  </button>
                </div>
              </div>
            )}

            {!isLoggedIn ? (
              // 비로그인 상태: Figma 디자인과 동일한 화면
              <>
                {/* 타이틀 + 설명 - 모바일에서는 중앙 정렬 */}
                <div
                  className={`flex flex-col items-center w-full ${
                    !isMd ? "flex-1 justify-center" : "justify-center"
                  }`}
                >
                  <AirTitle
                    mode="socrates"
                    title="소크라테스가 처음이신가요?"
                    subtitle={`자기소개서, 탐구보고서, 수업 내용도 OK!\n입력만하면 소크라테스가 질문을 시작합니다.`}
                  />
                </div>

                {/* PC에서만 버튼 표시 (모바일은 하단 고정) */}
                {!isMobileGuest && (
                  <Btn.Primary
                    onClick={handleLoginClick}
                    style="fill"
                    color="main"
                    className="px-10 py-0"
                  >
                    로그인 후 면접보기
                  </Btn.Primary>
                )}
              </>
            ) : !currentInterviewId ? (
              // 로그인 후 첫 이용 또는 면접 목록: 면접 카드 표시
              <div
                className={`w-full ${
                  !isMd
                    ? "flex-1 flex items-center justify-center"
                    : "flex flex-col items-center justify-center gap-8"
                }`}
              >
                <SocratesInterviewList
                  onStartInterview={handleStartInterview}
                />
                {/* 데스크탑: 버튼을 콘텐츠 영역에 표시 */}
                {isMd && (
                  <button
                    onClick={handleCreateSocrates}
                    className="h-12 px-10 rounded-[24px] text-white font-bold text-base tracking-[-0.32px] transition-all duration-200 bg-[#6d2ef2] hover:bg-[#5d25d9]"
                  >
                    새로운 소크라테스 만들기
                  </button>
                )}
              </div>
            ) : (
              // 면접 ID가 있을 때: 채팅 컨테이너 표시 (메시지 유무와 관계없이)
              <SocratesChatContainer interviewId={currentInterviewId} />
            )}
          </div>
        </div>

        {/* Free Scroll Button - 사용자가 위로 스크롤했을 때 표시 */}
        {isFreeScroll && !fetching && hasMessages && (
          <div className="absolute bottom-28 right-8">
            <Btn.Fab
              color="light"
              onClick={disableFreeScrollAndScroll}
              aria-label="Scroll to bottom"
            >
              <Chevron className="w-6 h-6 rotate-90" />
            </Btn.Fab>
          </div>
        )}

        {/* Footer with Prompt Input or Login CTA */}
        {isMobileGuest ? (
          // 모바일 비로그인: 하단 고정 로그인 버튼
          <div className="flex-shrink-0 flex flex-col items-center justify-center p-4 w-full bg-white">
            <button
              onClick={redirectToLogin}
              className="w-full max-w-[800px] h-12 rounded-[24px] bg-gradient-to-r from-[#cd3ec7] via-[#5328df] to-[#2d8efa] hover:from-[#ab38a6] hover:via-[#4927b9] hover:to-[#2b78cf] text-white font-bold text-base tracking-[-0.32px] transition-all duration-200"
            >
              로그인 후 면접보기
            </button>
          </div>
        ) : isLoggedIn && currentInterviewId ? (
          <div
            className="flex-shrink-0 flex flex-col items-center justify-end p-4 w-full bg-gradient-to-b from-transparent via-white/20 to-white pb-safe"
            style={{
              padding: !isMd && isLoggedIn ? "16px 0 0" : undefined,
              paddingBottom: !isMd
                ? "env(safe-area-inset-bottom, 16px)"
                : undefined,
            }}
          >
            <SocratesPromptContainer />
          </div>
        ) : isLoggedIn && !currentInterviewId && !isMd ? (
          // 모바일: 로그인 후 면접 목록 화면 - 새로운 소크라테스 만들기 버튼
          <div className="flex-shrink-0 flex flex-col items-center justify-center p-4 w-full bg-white">
            <button
              onClick={handleCreateSocrates}
              className="h-12 w-full max-w-[800px] rounded-[24px] text-white font-bold text-base tracking-[-0.32px] transition-all duration-200 bg-gradient-to-r from-[#cd3ec7] via-[#5328df] to-[#2d8efa] hover:from-[#ab38a6] hover:via-[#4927b9] hover:to-[#2b78cf]"
            >
              새로운 소크라테스 만들기
            </button>
          </div>
        ) : null}
      </div>

      {/* Share Modal */}
      <Modal
        type="sub"
        open={shareModalOpen}
        onOpenChange={handleOpenChange}
        shareUrl={shareUrl}
        onCopy={onCopy}
        copySuccess={copySuccess}
      />

      {/* 면접 이탈 확인 팝업 */}
      <ConfirmModal
        open={showExitConfirm}
        onOpenChange={handleExitCancel}
        title="면접 중단"
        message={"면접을 그만할까요?\n끝까지 완료해야 평가 리포트를 확인할 수 있어요."}
        confirmText="중단하고 나가기"
        onConfirm={handleExitConfirm}
      />

      {/* Login Modal */}
      {isLogin && <LoginModal />}
    </div>
  );
};

export default SocratesV2;
